/**
 * Pages on TanStack Query: the persisted page tree (seeded by bootstrap, refreshed with GET /pages), per-page
 * metadata (GET /pages/:id), page events, and optimistic page writes (rename, icon, move, trash, favourite) queued
 * while offline and replayed in order. Pending writes are re-applied on top of every refetch.
 * Page bodies are not here: they are Loro docs (platform/docs).
 */
import type { BootstrapResponse, GetPageResponse, PageNode, PersistentEvent, UpdatePageRequest } from "@bundle/contracts";
import { Docs, errorKind, type ApiClient, type DocsCache } from "@bundle/core";
import { MutationObserver, type Mutation, type QueryClient } from "@tanstack/query-core";

export const pageKeys = {
  tree: (ws: string) => ["ws", ws, "pages"] as const,
  pageAll: (ws: string) => ["ws", ws, "page"] as const,
  page: (ws: string, pageId: string) => ["ws", ws, "page", pageId] as const,
};

/** Page writes run one at a time, in order. */
export const PAGE_SCOPE = { id: "pages" };

export type UpdatePageVars = { kind: "updatePage"; ws: string; pageId: string; req: UpdatePageRequest; meId: string; at: number };
export type FavoriteVars = { kind: "favorite"; ws: string; pageId: string; on: boolean };
export type PageVars = UpdatePageVars | FavoriteVars;

function applyWrite(c: DocsCache, v: PageVars): DocsCache {
  return v.kind === "favorite" ? Docs.setFavorite(c, v.pageId, v.on) : Docs.applyUpdate(c, v.pageId, v.req, v.meId, v.at);
}

function isPageMutation(m: Mutation<unknown, Error, unknown, unknown>): boolean {
  const key = m.options.mutationKey;
  return Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("page.");
}

/** Writes not yet confirmed (in flight, waiting to retry, or paused offline), oldest first. */
function pendingWrites(client: QueryClient, ws: string): PageVars[] {
  return client
    .getMutationCache()
    .getAll()
    .filter((m) => isPageMutation(m) && (m.state.status === "pending" || m.state.isPaused))
    .map((m) => m.state.variables as PageVars | undefined)
    .filter((v): v is PageVars => v !== undefined && v.ws === ws);
}

function withPending(client: QueryClient, ws: string, cache: DocsCache, except?: PageVars): DocsCache {
  let c = cache;
  for (const v of pendingWrites(client, ws)) if (v !== except) c = applyWrite(c, v);
  return c;
}

export function updateTree(client: QueryClient, ws: string, f: (c: DocsCache) => DocsCache): void {
  client.setQueryData<DocsCache>(pageKeys.tree(ws), (c) => (c === undefined ? c : f(c)));
}

function updatePageMeta(client: QueryClient, ws: string, page: PageNode): void {
  client.setQueryData<GetPageResponse>(pageKeys.page(ws, page.id), (r) => (r === undefined ? r : { ...r, page }));
}

// ---------- Queries ----------

/** Bootstrap carries the tree (M5+ servers): seed it so the sidebar never waits for a second request. */
export function seedTree(client: QueryClient, ws: string, b: BootstrapResponse): void {
  const tree = Docs.fromBootstrap(b);
  if (tree !== undefined) client.setQueryData<DocsCache>(pageKeys.tree(ws), withPending(client, ws, tree));
}

export async function fetchTree(client: QueryClient, api: ApiClient, ws: string): Promise<DocsCache> {
  return withPending(client, ws, Docs.fromList(await api.listPages(ws)));
}

export async function fetchPage(client: QueryClient, api: ApiClient, ws: string, pageId: string): Promise<GetPageResponse> {
  const res = await api.getPage(ws, pageId);
  // Keep the tree in step with what the server just said about this page.
  if (res.page.kind !== "row") updateTree(client, ws, (c) => (c.pages[res.page.id] === undefined ? c : withPending(client, ws, Docs.upsertPages(c, [res.page]))));
  return res;
}

// ---------- Events ----------

export function applyPageEvent(client: QueryClient, ws: string, ev: PersistentEvent): void {
  if (ev.type !== "page.created" && ev.type !== "page.updated" && ev.type !== "page.removed" && ev.type !== "database.updated") return;
  updateTree(client, ws, (c) => withPending(client, ws, Docs.applyEvent(c, ev)));
  if (ev.type === "page.created" || ev.type === "page.updated") updatePageMeta(client, ws, ev.page);
}

// ---------- Mutations ----------

export function mutatePage(client: QueryClient, vars: PageVars): void {
  new MutationObserver<unknown, unknown, PageVars>(client, { mutationKey: [`page.${vars.kind}`], scope: PAGE_SCOPE }).mutate(vars).catch(() => {
    // Failures roll back through a refetch (see registerPageMutations).
  });
}

export function registerPageMutations(client: QueryClient, api: ApiClient): void {
  const rollback = (e: unknown, v: PageVars) => {
    if (errorKind(e) !== "network") void client.invalidateQueries({ queryKey: pageKeys.tree(v.ws) });
  };
  client.setMutationDefaults(["page.updatePage"], {
    scope: PAGE_SCOPE,
    mutationFn: (v: UpdatePageVars) => api.updatePage(v.ws, v.pageId, v.req),
    onMutate: (v: UpdatePageVars) => {
      updateTree(client, v.ws, (c) => applyWrite(c, v));
      const meta = client.getQueryData<GetPageResponse>(pageKeys.page(v.ws, v.pageId));
      if (meta !== undefined && v.req.title !== undefined) updatePageMeta(client, v.ws, { ...meta.page, title: v.req.title });
    },
    onSuccess: (page: PageNode, v: UpdatePageVars) => {
      updateTree(client, v.ws, (c) => withPending(client, v.ws, Docs.upsertPages(c, [page]), v));
      updatePageMeta(client, v.ws, page);
    },
    onError: rollback,
  });
  client.setMutationDefaults(["page.favorite"], {
    scope: PAGE_SCOPE,
    mutationFn: (v: FavoriteVars) => api.setFavorite(v.ws, v.pageId, v.on),
    onMutate: (v: FavoriteVars) => updateTree(client, v.ws, (c) => applyWrite(c, v)),
    onError: rollback,
  });
}
